/**
 * Numeric definite integrals with an error estimate and honest warnings.
 *
 * Method (Piessens et al., QUADPACK; Burden & Faires §4.7, Press et al. §4.5):
 *
 * - Global adaptive Gauss–Kronrod. Each interval is integrated with the 15-point
 *   Kronrod rule; the difference from the embedded 7-point Gauss rule is the error
 *   estimate (scaled as QUADPACK's QK15 does); the interval with the largest error is
 *   bisected until the total error meets the tolerance or the limit is reached.
 * - Infinite limits map to [0, 1) with x = a + t/(1 − t) (and its mirror image).
 * - When an endpoint is singular (f is infinite there, as for 1/√x or ln x) or the
 *   adaptive rule does not converge, the double-exponential (tanh–sinh) rule is run as
 *   well: it is exact in the limit for integrable endpoint singularities.
 * - If neither converges, the integral is tested for divergence by truncation: the
 *   integral away from the bad point, ∫ over [a, x* − δ] and [x* + δ, b], is computed
 *   for δ = 10⁻³, 10⁻⁶, 10⁻⁹. Increments that fail to shrink mean divergence.
 *
 * Nothing here knows about expressions: it takes a plain function of one variable.
 */

export type Integrand = (x: number) => number;

export type IntegralWarning =
  | { code: "undefined-region"; at: number }
  | { code: "singular-endpoint"; at: number }
  | { code: "singular-interior"; at: number }
  | { code: "infinite-limit" }
  | { code: "not-converged"; at: number }
  | { code: "divergent"; at: number }
  | { code: "methods-disagree"; other: number };

export interface IntegralResult {
  /** The estimate; NaN when the integrand is undefined on part of the interval. */
  value: number;
  /** Estimated absolute error. */
  error: number;
  evaluations: number;
  /** Subintervals in the final adaptive partition. */
  intervals: number;
  method: "gauss-kronrod" | "tanh-sinh";
  converged: boolean;
  warnings: IntegralWarning[];
}

export interface IntegralOptions {
  absoluteTolerance?: number;
  relativeTolerance?: number;
  maxIntervals?: number;
}

/* ---------------------------------------------- Gauss–Kronrod 7 / 15 rule */

/** Kronrod nodes on [0, 1] (the rule is symmetric), from QUADPACK QK15. */
const XGK = [
  0.991_455_371_120_812_6, 0.949_107_912_342_758_5, 0.864_864_423_359_769_1,
  0.741_531_185_599_394_5, 0.586_087_235_467_691_1, 0.405_845_151_377_397_2,
  0.207_784_955_007_898_48, 0,
];
const WGK = [
  0.022_935_322_010_529_224, 0.063_092_092_629_978_56, 0.104_790_010_322_250_19,
  0.140_653_259_715_525_92, 0.169_004_726_639_267_9, 0.190_350_578_064_785_42,
  0.204_432_940_075_298_89, 0.209_482_141_084_727_82,
];
/** Gauss weights for the 7-point rule at the Kronrod nodes with odd index. */
const WG = [
  0.129_484_966_168_869_7, 0.279_705_391_489_276_64, 0.381_830_050_505_118_9,
  0.417_959_183_673_469_4,
];

interface Piece {
  a: number;
  b: number;
  value: number;
  error: number;
}

const EPSILON = Number.EPSILON;
const INITIAL_PANELS = 8;
const MAX_SPLIT_DEPTH = 3;
const UNDERFLOW = Number.MIN_VALUE / EPSILON;

/** One Gauss–Kronrod panel: value and QUADPACK's scaled error estimate. */
function kronrodPanel(
  f: Integrand,
  a: number,
  b: number
): Piece & { bad: number | null } {
  const centre = (a + b) / 2;
  const half = (b - a) / 2;
  const fc = f(centre);
  let bad: number | null = Number.isFinite(fc) ? null : centre;
  let resultK = fc * WGK[7];
  let resultG = fc * WG[3];
  let resultAbs = Math.abs(resultK);
  const values: [number, number][] = [];
  for (let j = 0; j < 7; j += 1) {
    const offset = half * XGK[j];
    const f1 = f(centre - offset);
    const f2 = f(centre + offset);
    if (bad === null && !(Number.isFinite(f1) && Number.isFinite(f2))) {
      bad = Number.isFinite(f1) ? centre + offset : centre - offset;
    }
    values.push([f1, f2]);
    resultK += WGK[j] * (f1 + f2);
    resultAbs += WGK[j] * (Math.abs(f1) + Math.abs(f2));
    if (j % 2 === 1) {
      resultG += WG[(j - 1) / 2] * (f1 + f2);
    }
  }
  const mean = resultK / 2;
  let deviation = WGK[7] * Math.abs(fc - mean);
  for (let j = 0; j < 7; j += 1) {
    deviation +=
      WGK[j] * (Math.abs(values[j][0] - mean) + Math.abs(values[j][1] - mean));
  }
  const value = resultK * half;
  resultAbs *= Math.abs(half);
  deviation *= Math.abs(half);
  let error = Math.abs((resultK - resultG) * half);
  if (deviation !== 0 && error !== 0) {
    error = deviation * Math.min(1, ((200 * error) / deviation) ** 1.5);
  }
  if (resultAbs > UNDERFLOW) {
    error = Math.max(50 * EPSILON * resultAbs, error);
  }
  return { a, b, value, error, bad };
}

interface AdaptiveOutcome {
  value: number;
  error: number;
  evaluations: number;
  pieces: Piece[];
  converged: boolean;
  /** An interior point where the integrand was not finite. */
  undefinedAt: number | null;
  /** Midpoint of the worst subinterval when it did not converge. */
  worst: number | null;
}

function adaptive(
  f: Integrand,
  a: number,
  b: number,
  absTol: number,
  relTol: number,
  maxIntervals: number
): AdaptiveOutcome {
  // Start from a few equal panels, not one: a narrow spike in the middle of a wide
  // interval would otherwise fall between the 15 sample points of a single panel.
  const pieces: Piece[] = [];
  let undefinedAt: number | null = null;
  let evaluations = 0;
  for (let i = 0; i < INITIAL_PANELS; i += 1) {
    const panel = kronrodPanel(
      f,
      a + ((b - a) * i) / INITIAL_PANELS,
      a + ((b - a) * (i + 1)) / INITIAL_PANELS
    );
    pieces.push(panel);
    undefinedAt = undefinedAt ?? panel.bad;
    evaluations += 15;
  }
  let total = pieces.reduce((sum, p) => sum + p.value, 0);
  let errorSum = pieces.reduce((sum, p) => sum + p.error, 0);
  while (
    errorSum > Math.max(absTol, relTol * Math.abs(total)) &&
    pieces.length < maxIntervals &&
    undefinedAt === null
  ) {
    let worst = 0;
    for (let i = 1; i < pieces.length; i += 1) {
      if (pieces[i].error > pieces[worst].error) {
        worst = i;
      }
    }
    const piece = pieces[worst];
    const mid = (piece.a + piece.b) / 2;
    if (mid === piece.a || mid === piece.b) {
      break;
    }
    const left = kronrodPanel(f, piece.a, mid);
    const right = kronrodPanel(f, mid, piece.b);
    evaluations += 30;
    undefinedAt = left.bad ?? right.bad;
    pieces.splice(worst, 1, left, right);
    total = pieces.reduce((sum, p) => sum + p.value, 0);
    errorSum = pieces.reduce((sum, p) => sum + p.error, 0);
  }
  const converged =
    undefinedAt === null &&
    errorSum <= Math.max(absTol, relTol * Math.abs(total));
  let worstPiece = pieces[0];
  for (const piece of pieces) {
    if (piece.error > worstPiece.error) {
      worstPiece = piece;
    }
  }
  return {
    value: total,
    error: errorSum,
    evaluations,
    pieces,
    converged,
    undefinedAt,
    worst: converged ? null : (worstPiece.a + worstPiece.b) / 2,
  };
}

/* --------------------------------------------------------------- tanh–sinh */

interface TanhSinhOutcome {
  value: number;
  error: number;
  evaluations: number;
  converged: boolean;
}

const MAX_T = 4;
/** Points beyond this |t| form the tail used to test that truncating at MAX_T is harmless. */
const HEAD_T = 3.5;

/**
 * Double-exponential quadrature on [a, b]; handles integrable endpoint singularities.
 *
 * The rule has to stop somewhere (|t| ≤ 4, within 10⁻³⁷ of an endpoint). For an
 * integrable singularity the mass in the last stretch is negligible; for a divergent one
 * (1/x) it is not, so the rule is accepted only if dropping |t| > 3.5 changes nothing.
 */
function tanhSinh(
  f: Integrand,
  a: number,
  b: number,
  relTol: number
): TanhSinhOutcome {
  const width = b - a;
  let evaluations = 0;
  let previous = Number.NaN;
  let head = 0;
  let tail = 0;
  let step = 1;
  const point = (t: number): number => {
    const u = Math.PI * Math.sinh(t);
    const lowerTail = 1 / (1 + Math.exp(-u));
    const upperTail = 1 / (1 + Math.exp(u));
    const x = t <= 0 ? a + width * lowerTail : b - width * upperTail;
    if (x <= a || x >= b) {
      return 0;
    }
    const weight = width * Math.PI * Math.cosh(t) * lowerTail * upperTail;
    const y = f(x);
    evaluations += 1;
    return Number.isFinite(y) ? y * weight : 0;
  };
  const add = (t: number) => {
    const value = point(t);
    if (Math.abs(t) <= HEAD_T) {
      head += value;
    } else {
      tail += value;
    }
  };
  add(0);
  let result = head * step;
  for (let level = 0; level <= 9; level += 1) {
    // Level 0 samples t = ±1, ±2, …; each later level adds the midpoints of the last.
    const stride = level === 0 ? 1 : 2;
    for (let j = 1; j * step <= MAX_T; j += stride) {
      add(j * step);
      add(-j * step);
    }
    result = (head + tail) * step;
    const change = Math.abs(result - previous);
    const truncation = Math.abs(tail * step);
    const tolerance = Math.max(relTol * Math.abs(result), 1e-13);
    if (level >= 2 && change <= tolerance && truncation <= tolerance) {
      return {
        value: result,
        error: change + truncation,
        evaluations,
        converged: true,
      };
    }
    previous = result;
    step /= 2;
  }
  return {
    value: result,
    error: Math.abs(result - previous) + Math.abs(tail * step * 2),
    evaluations,
    converged: false,
  };
}

/* ------------------------------------------------------------- the driver */

const DEFAULTS = {
  absoluteTolerance: 1e-12,
  relativeTolerance: 1e-10,
  maxIntervals: 400,
};

function fraction(index: number): number {
  return 10 ** (-3 * index);
}

/**
 * Does the integral keep growing as the integration stops closer to a bad point? Each
 * side is tested on its own so an even-odd cancellation (a principal value) cannot hide
 * a divergence.
 */
function growsWithoutBound(
  f: Integrand,
  a: number,
  b: number,
  at: number,
  options: typeof DEFAULTS
): boolean {
  const sides: [number, number, 1 | -1][] = [];
  if (at > a) {
    sides.push([a, at, -1]);
  }
  if (at < b) {
    sides.push([at, b, 1]);
  }
  return sides.some(([lo, hi, direction]) => {
    const length = hi - lo;
    const partial = [1, 2, 3, 4].map((k) => {
      const cut = length * fraction(k);
      const outcome =
        direction === -1
          ? adaptive(f, lo, hi - cut, options.absoluteTolerance, 1e-8, 200)
          : adaptive(f, lo + cut, hi, options.absoluteTolerance, 1e-8, 200);
      return outcome.value;
    });
    const [, d2, d3] = [1, 2, 3].map((i) =>
      Math.abs(partial[i] - partial[i - 1])
    );
    const negligible = 1e-9 * Math.max(1, Math.abs(partial[3]));
    // A convergent singularity shrinks the increment by a fixed factor per three
    // decades (10^-1.5 for 1/√x); a divergent one does not shrink at all.
    return d3 > negligible && d3 > 0.7 * d2;
  });
}

/**
 * A sample that rounds onto an endpoint (t = 1 − 10⁻¹⁷ is 1) is not a singularity in the
 * interior; it carries no weight, so it counts as 0.
 */
function guardEndpoints(f: Integrand, a: number, b: number): Integrand {
  const reach = 1e-13 * (b - a);
  return (x) => {
    const y = f(x);
    if (Number.isFinite(y)) {
      return y;
    }
    return x - a <= reach || b - x <= reach ? 0 : y;
  };
}

/** Is the worst subinterval within 0.1% of the width from an endpoint? */
function nearEndpoint(at: number | null, a: number, b: number): boolean {
  return at !== null && (at - a < 1e-3 * (b - a) || b - at < 1e-3 * (b - a));
}

function integrateFinite(
  raw: Integrand,
  a: number,
  b: number,
  options: typeof DEFAULTS,
  depth = 0
): IntegralResult {
  const warnings: IntegralWarning[] = [];
  for (const end of [a, b]) {
    if (!Number.isFinite(raw(end))) {
      warnings.push({ code: "singular-endpoint", at: end });
    }
  }
  const f = guardEndpoints(raw, a, b);
  const run = adaptive(
    f,
    a,
    b,
    options.absoluteTolerance,
    options.relativeTolerance,
    options.maxIntervals
  );
  if (run.undefinedAt !== null) {
    return singularResult(f, a, b, run, warnings, options, depth);
  }
  const endpointTrouble = warnings.length > 0;
  if (run.converged && !endpointTrouble) {
    return {
      value: run.value,
      error: run.error,
      evaluations: run.evaluations,
      intervals: run.pieces.length,
      method: "gauss-kronrod",
      converged: true,
      warnings,
    };
  }
  // The double-exponential rule only where the trouble is at an endpoint: elsewhere its
  // symmetric nodes would quietly return a principal value for a divergent integral.
  const alt =
    endpointTrouble || nearEndpoint(run.worst, a, b)
      ? tanhSinh(f, a, b, 1e-10)
      : null;
  if (alt?.converged) {
    if (
      run.converged &&
      Math.abs(alt.value - run.value) > 1e-6 * Math.max(1, Math.abs(alt.value))
    ) {
      warnings.push({ code: "methods-disagree", other: run.value });
    }
    return {
      value: alt.value,
      // Near a singular endpoint the integrand itself loses digits (1 − x² for x → 1),
      // which no convergence test can see: never claim better than eight digits.
      error: Math.max(alt.error, 1e-8 * Math.abs(alt.value)),
      evaluations: run.evaluations + alt.evaluations,
      intervals: run.pieces.length,
      method: "tanh-sinh",
      converged: true,
      warnings,
    };
  }
  const at = run.worst ?? (a + b) / 2;
  const divergent = growsWithoutBound(f, a, b, at, options);
  warnings.push(
    divergent ? { code: "divergent", at } : { code: "not-converged", at }
  );
  return {
    value: divergent ? Number.NaN : run.value,
    error: run.error,
    evaluations: run.evaluations + (alt?.evaluations ?? 0),
    intervals: run.pieces.length,
    method: "gauss-kronrod",
    converged: false,
    warnings,
  };
}

/**
 * The integrand was not finite at a sampled interior point. If that point is a pole the
 * integral may still exist as an improper integral only when the singularity is
 * integrable, which the truncation test decides; if the function is simply undefined
 * (sqrt of a negative, ln of a non-positive), there is no real integral.
 */
function singularResult(
  f: Integrand,
  a: number,
  b: number,
  run: AdaptiveOutcome,
  warnings: IntegralWarning[],
  options: typeof DEFAULTS,
  depth: number
): IntegralResult {
  const at = run.undefinedAt as number;
  if (depth >= MAX_SPLIT_DEPTH) {
    warnings.push({ code: "not-converged", at });
    return {
      value: Number.NaN,
      error: Number.NaN,
      evaluations: run.evaluations,
      intervals: run.pieces.length,
      method: "gauss-kronrod",
      converged: false,
      warnings,
    };
  }
  // Probe either side of the point: a pole is infinite or huge near it, a domain gap is NaN.
  const near = 1e-9 * Math.max(1, Math.abs(at));
  const probes = [f(at - near), f(at + near)];
  if (probes.some(Number.isNaN)) {
    warnings.push({ code: "undefined-region", at });
    return {
      value: Number.NaN,
      error: Number.NaN,
      evaluations: run.evaluations,
      intervals: run.pieces.length,
      method: "gauss-kronrod",
      converged: false,
      warnings,
    };
  }
  // The function is finite on both sides of the point (a pole, or a removable gap like
  // sin(x)/x at 0): integrate each side up to the point, where the end rules never look.
  const split = combine(
    integrateFinite(f, a, at, options, depth + 1),
    integrateFinite(f, at, b, options, depth + 1)
  );
  return {
    ...split,
    warnings: [
      { code: "singular-interior", at },
      ...split.warnings.filter(
        (w) => !(w.code === "singular-endpoint" && w.at === at)
      ),
      ...warnings,
    ],
  };
}

/** [a, ∞) → [0, 1): x = a + t/(1 − t), dx = dt/(1 − t)². */
function toUnitFromBelow(f: Integrand, a: number): Integrand {
  return (t) => {
    const s = 1 - t;
    return f(a + t / s) / (s * s);
  };
}

/** (−∞, b] → [0, 1): x = b − t/(1 − t). */
function toUnitFromAbove(f: Integrand, b: number): Integrand {
  return (t) => {
    const s = 1 - t;
    return f(b - t / s) / (s * s);
  };
}

function combine(left: IntegralResult, right: IntegralResult): IntegralResult {
  return {
    value: left.value + right.value,
    error: left.error + right.error,
    evaluations: left.evaluations + right.evaluations,
    intervals: left.intervals + right.intervals,
    method: left.method === right.method ? left.method : "gauss-kronrod",
    converged: left.converged && right.converged,
    warnings: [...left.warnings, ...right.warnings],
  };
}

/**
 * ∫ₐᵇ f(x) dx for finite or infinite limits, a ≠ b. The sign flips if a > b.
 */
export function integrate(
  f: Integrand,
  a: number,
  b: number,
  options: IntegralOptions = {}
): IntegralResult {
  const settings = { ...DEFAULTS, ...options };
  if (a === b) {
    return {
      value: 0,
      error: 0,
      evaluations: 0,
      intervals: 0,
      method: "gauss-kronrod",
      converged: true,
      warnings: [],
    };
  }
  if (a > b) {
    const flipped = integrate(f, b, a, options);
    return { ...flipped, value: -flipped.value };
  }
  const infiniteLower = a === Number.NEGATIVE_INFINITY;
  const infiniteUpper = b === Number.POSITIVE_INFINITY;
  if (!(infiniteLower || infiniteUpper)) {
    return integrateFinite(f, a, b, settings);
  }
  const marker: IntegralWarning = { code: "infinite-limit" };
  let result: IntegralResult;
  if (infiniteLower && infiniteUpper) {
    result = combine(
      integrateFinite(toUnitFromAbove(f, 0), 0, 1, settings),
      integrateFinite(toUnitFromBelow(f, 0), 0, 1, settings)
    );
  } else if (infiniteUpper) {
    result = integrateFinite(toUnitFromBelow(f, a), 0, 1, settings);
  } else {
    result = integrateFinite(toUnitFromAbove(f, b), 0, 1, settings);
  }
  return {
    ...result,
    warnings: [
      marker,
      // Endpoint warnings from the substituted variable t are artefacts (t = 1 is x = ∞);
      // only a finite original limit where f itself is not finite is reported.
      ...[a, b]
        .filter((end) => Number.isFinite(end) && !Number.isFinite(f(end)))
        .map((at): IntegralWarning => ({ code: "singular-endpoint", at })),
      ...result.warnings.filter(
        (w) => w.code !== "infinite-limit" && w.code !== "singular-endpoint"
      ),
    ],
  };
}

/**
 * Total (unsigned) area ∫|f| and net signed area on [a, b], so the caller can show both
 * when f changes sign.
 */
export function totalArea(
  f: Integrand,
  a: number,
  b: number,
  options: IntegralOptions = {}
): IntegralResult {
  return integrate((x) => Math.abs(f(x)), a, b, options);
}
